/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { DealerApiCommunications } from '../../webmodule-common/other/api/dealer-api-communications';
import { displayFrameConfigLoadErrorMsg, GlobalSupplierNotification } from './supplier-services';
import { information } from '../../webmodule-common/other/ui/modal-option';
import { QuoteSupplier } from '../quotes/quote-service';
import { responseHandler } from '../api/api-response-handler';
import { sharedExecute } from '../../webmodule-common/other/common/helpers/callbacks';

import { ApiCommunications, V6OfflineCause } from '../../webmodule-common/interop/webmodule-interop';
import { LangV6Config } from '../../localization/scripts/v6config/v6config-lang';
import {
  v6bootloaderStale,
  v6FrameConfigScriptLoader,
  V6LoaderFailureReason
} from '../../webmodule-common/interop/interfaces/v6config-loader';

// Re-export v6bootloaderStale for backward compatibility
export { v6bootloaderStale };

let _v6commSingleton: ApiCommunications | undefined;
const v6ApiInjecterEvent = () => {
  if (!_v6commSingleton)
    _v6commSingleton = new DealerApiCommunications(globalThis.dealerConfiguration.v6apiHost, responseHandler, () => {
      //Redirect to home page, next query will force a login to occur
      window.location.href = '/login';
    });
  return _v6commSingleton;
};

export async function getQuoteSupplierFromApi(): Promise<QuoteSupplier[] | undefined> {
  //this is configured as an anonymous call so we dont need a login to get this data.
  const comms = v6ApiInjecterEvent();
  const r = await comms.post<unknown>(`api/v6/configitem/suppliers`, {});
  return (r as any)?.items.map(x => {
    const qs: QuoteSupplier = {
      supplierId: x.id,
      description: x.name,
      online: x.online,
      offlineCause: x.offlineCause
    };
    return qs;
  });
}

export interface V6FrameConfigVersion {
  api: number;
  major: number;
  minor: number;
}

// eslint-disable-next-line no-var -- var declared as it is replaced by
declare var __localdebug__: string;

// Track the last failure to avoid showing the same error message repeatedly
let _lastFailureReason: V6LoaderFailureReason | undefined;
let _lastFailureMessage: string | undefined;

export const v6BootLoader = sharedExecute(() => v6BootLoaderInternal(false));
export const v6BootLoaderWithFailMsg = sharedExecute(() => v6BootLoaderInternal(true));

async function v6BootLoaderInternal(alwaysShowFailMsg: boolean): Promise<boolean> {
  const comms = v6ApiInjecterEvent();

  // Call the core loader (handles stale checks and version mismatches internally)
  const result = await v6FrameConfigScriptLoader(comms);

  if (result.success) {
    // Reset failure tracking on success
    _lastFailureReason = undefined;
    _lastFailureMessage = undefined;
    return true;
  }

  // Determine if we should show the error message:
  // - alwaysShowFailMsg (withFailMsg): ALWAYS show the message
  // - otherwise: only show if this is a DIFFERENT failure from the last one
  const isDifferentFailure = _lastFailureReason !== result.failureReason || _lastFailureMessage !== result.message;
  const shouldShowMessage = alwaysShowFailMsg || isDifferentFailure;

  // Update last failure tracking
  _lastFailureReason = result.failureReason;
  _lastFailureMessage = result.message;

  if (shouldShowMessage) {
    switch (result.failureReason) {
      case V6LoaderFailureReason.SupplierOffline:
      case V6LoaderFailureReason.InvalidVersion:
        await GlobalSupplierNotification.showOfflineMessage(
          result.offlineCause ?? V6OfflineCause.Connection,
          result.message
        );
        break;

      case V6LoaderFailureReason.UnsupportedVersion:
        await information(LangV6Config.v6OptimizedBlobStorageInfo());
        break;

      case V6LoaderFailureReason.ScriptLoadFailed:
      case V6LoaderFailureReason.ConfigInitFailed:
        await displayFrameConfigLoadErrorMsg();
        break;
    }
  }

  return false;
}
